import { clsx, type ClassValue } from "clsx"
import { format } from "date-fns";
import { twMerge } from "tailwind-merge"
import { currencyCodes } from "./currencyCodes";
import * as XLSX from 'xlsx';
import { Transaction } from "../pages/Banking/components/BankingApis";
import moment from "moment";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Common function to capitalize the first letter of each word
export const capitalizeWords = (str: string): string => {
  if (!str) return "-"; // If the string is empty, return a hyphen
  return str
    .split(" ") // Split the string into words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize each word
    .join(" "); // Join the words back into a single string
};

export const formatMonthYear = (date: Date) => {
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  return `${months[date.getMonth()]} ${date.getFullYear()}`;
};

export function getYearFromDate(dateString: Date) {
  const date = new Date(dateString);
  return date.getFullYear();
}

export const isValidUUID = (id: string): boolean => {
  // Regular expression to check UUID format (8-4-4-4-12 characters)
  const uuidRegex =
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
  return uuidRegex.test(id);
};

export const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const pad = (n: number) => n.toString().padStart(2, "0");
  return (
    date.getFullYear() +
    "-" +
    pad(date.getMonth() + 1) +
    "-" +
    pad(date.getDate()) +
    "T" +
    pad(date.getHours()) +
    ":" +
    pad(date.getMinutes()) +
    ":" +
    pad(date.getSeconds())
  );
};

export const formatToShortDate = (dateString: string | undefined): string => {
  if (!dateString) return ""; // Return an empty string if the date is undefined or null
  try {
    return format(new Date(dateString), "MMM dd, yyyy"); // Format the date
  } catch (error) {
    console.error("Error formatting date:", error);
    return ""; // Return an empty string if formatting fails
  }
};

export function formatPhoneNumber(input: string): string {
  // Step 1: Check if the input is empty, return a hyphen if so
  if (!input) {
    return '-';
  }

  // Step 2: Remove all non-numeric characters
  const normalized = input.replace(/\D/g, '');

  // Step 3: If the normalized input is empty, return a hyphen
  if (!normalized) {
    return '-';
  }

  // Step 4: Format the phone number (e.g., (XXX) XXX-XXXX)
  const formatted = `(${normalized.slice(0, 3)}) ${normalized.slice(3, 6)}-${normalized.slice(6, 10)}`;

  return formatted;
}

export const currencyCode = (): string => {
  const code = localStorage.getItem("currencyCode") || "USD"
  return currencyCodes[code] || "$"; // Fallback if code is invalid
};

// utils/handleApiError.ts
export const handleApiError = (response: any, onError?: (error: Error) => void) => {
  if (response.errorDetail && response.errorDetail.length > 0) {
    // Show the error messages from errorDetail if present
    const errorMessage = response.errorDetail
      ?.map((err: any) => err.errorMessage)
      .join(", ");
    onError?.(new Error(errorMessage)); // Call onError to notify user with the message
  } else if (response.message) {
    // If errorDetail is empty but there's a message, use that
    onError?.(new Error(response.message)); // Pass the message to onError
  } else {
    // Generic fallback error if no specific error message or details
    onError?.(
      new Error("An unexpected error occurred while processing the request.")
    );
  }
};

export function deepMerge(target: any, source: any) {
  const output = { ...target };
  if (typeof source !== "object" || source === null) return output;
  Object.keys(source).forEach(key => {
    if (
      typeof source[key] === "object" &&
      source[key] !== null &&
      !Array.isArray(source[key])
    ) {
      output[key] = deepMerge(target[key] || {}, source[key]);
    } else {
      output[key] = source[key];
    }
  });
  return output;
}

export function parseBalance(balance: string): number {
  return Number(balance.replace(/[^0-9.-]+/g, ""));
}

export function formatBalance(balance: string): string {
  const num = parseBalance(balance);
  const abs = Math.abs(num).toFixed(2);
  const code = currencyCode();
  if (num < 0) {
    return `(${code}${abs})`;
  }
  return `${code}${abs}`;
}

interface DownloadReportOptions {
  data: any;
  fileName: string;
  format: 'excel';
  type: 'profit-loss' | 'balance-sheet' | 'chart-of-accounts' | 'journal' | "transactions";
  selectedCategory?: number | null;
}

export const downloadReport = async ({ data, fileName, format, type, selectedCategory = null }: DownloadReportOptions) => {
  try {
    if (format === 'excel') {
      return downloadExcel(data, fileName, type, selectedCategory);
    }
    throw new Error('Unsupported format');
  } catch (error) {
    console.error('Error downloading report:', error);
    throw error;
  }
};

const downloadExcel = async (data: any, fileName: string, type: string, selectedCategory: number | null) => {
  let worksheet;
  if (type === "journal") {
    // For journal, handle download and return early
    downloadJournalByMonth(data, fileName);
    return;
  }
  switch (type) {
    case 'profit-loss':
      worksheet = formatProfitLossForExcel(data);
      break;
    case 'balance-sheet':
      worksheet = formatBalanceSheetForExcel(data);
      break;
    case "chart-of-accounts":
      worksheet = formatChartOfAccountsForExcel(data);
      break;
    case "transactions":
      worksheet = formatTransactionsForExcel(data, selectedCategory);
      break;
    default:
      throw new Error('Unsupported report type');
  }

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, type);
  XLSX.writeFile(workbook, `${fileName}.xlsx`);
};

function getMonthKey(dateStr: string) {
  // Accepts "6/4/2025" or "2025-06-04" etc.
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "Unknown";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

const downloadJournalByMonth = (data: any[], fileName: string) => {
  // 1. Group by month
  const grouped: Record<string, any[]> = {};
  for (let i = data.length - 1; i >= 0; i--) {
    const voucher = data[i];
    const monthKey = getMonthKey(voucher.voucherDate);
    if (!grouped[monthKey]) grouped[monthKey] = [];
    grouped[monthKey].push(voucher);
  }

  // 2. Create workbook
  const workbook = XLSX.utils.book_new();

  // 3. For each month, create a worksheet
  Object.entries(grouped).forEach(([month, vouchers]) => {
    const rows: any[] = [];
    rows.push([
      "Journal #",
      "Date",
      "Entry Type",
      "Account Name",
      "Debit",
      "Credit"
    ]);
    vouchers.forEach((voucher: any) => {
      let debitTotal = 0;
      let creditTotal = 0;
      if (voucher.children && voucher.children.length > 0) {
        voucher.children.forEach((entry: any, idx: number) => {
          rows.push([
            idx === 0 ? voucher.voucherId : '',
            idx === 0 ? formatToShortDate(voucher.voucherDate) : '',
            idx === 0 ? voucher.transactionType : '',
            entry.accountName,
            entry.debitAmt !== undefined ? formatUniversalNumber(entry.debitAmt) : '',
            entry.creditAmt !== undefined ? formatUniversalNumber(entry.creditAmt) : ''
          ]);
          debitTotal += entry.debitAmt || 0;
          creditTotal += entry.creditAmt || 0;
        });
        rows.push(['', '', '', 'Total', formatUniversalNumber(debitTotal), formatUniversalNumber(creditTotal)]);
        rows.push(['', '', '', '', '', '']);
      }
    });
    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    worksheet['!cols'] = [
      { wch: 12 }, // Journal #
      { wch: 14 }, // Date
      { wch: 22 }, // Entry Type
      { wch: 22 }, // Account Name
      { wch: 12 }, // Debit
      { wch: 12 }, // Credit
    ];
    XLSX.utils.book_append_sheet(workbook, worksheet, month);
  });

  // 4. Save the workbook
  XLSX.writeFile(workbook, `${fileName}.xlsx`);
};

const formatChartOfAccountsForExcel = (data: any) => {
  const rows: any[] = [];

  // Add headers
  rows.push(["Account", "Amount"]);
  rows.push(['', '']);

  // Process each main category (ASSET, EXPENSE, LIABILITY, REVENUE, etc.)
  data.forEach((category: any) => {
    // Section header (ASSET, EXPENSE, etc.)
    rows.push([category.name, '']);

    // Process subcategories and accounts
    category.children?.forEach((subCategory: any) => {
      subCategory.children?.forEach((account: any) => {
        rows.push([
          account.name,
          formatUniversalNumber(parseFloat(account.balance))
        ]);
      });
    });

    // Blank row after each section
    rows.push(['', '']);
  });

  // Create worksheet
  const worksheet = XLSX.utils.aoa_to_sheet(rows);

  // Set column widths
  worksheet['!cols'] = [
    { wch: 30 }, // Account
    { wch: 15 }, // Amount
  ];

  return worksheet;
};

export const formatTransactionsForExcel = (
  transactions: Transaction[],
  selectedCategory: number | null
) => {
  const rows: any[] = [];

  let headers: string[] = [];
  let fieldExtractor: (txn: Transaction) => any[] = () => [];
  if (selectedCategory === 47) {
    // Use categorized columns (e.g., categorized view)
    headers = [
      "Transaction Date",
      "Transaction Name",
      "Amount",
      "From/To",
    ];
    fieldExtractor = (txn) => [
      txn.transactionDate
        ? format(new Date(txn.transactionDate), "yyyy-MM-dd")
        : "-",
      txn.transactionName?.trim() || "-",
      formatUniversalNumber(txn.amount),
      txn.merchantName?.trim() || "-",
    ];
  } else if (selectedCategory === 46) {
    // forReviewColumns
    headers = [
      "Transaction Date",
      "Description",
      "From/To",
      "Amount",
    ];
    fieldExtractor = (txn) => [
      txn.transactionDate
        ? format(new Date(txn.transactionDate), "yyyy-MM-dd")
        : "-",
      txn.personalFinanceCategoryDetailed?.trim() || "-",
      txn.merchantName?.trim() || "-",
      formatUniversalNumber(txn.amount),
    ];
  } else if (selectedCategory === 49) {
    // exludedColumns
    headers = ["Transaction Date", "Transaction Name", "Amount"];
    fieldExtractor = (txn) => [
      txn.transactionDate
        ? format(new Date(txn.transactionDate), "yyyy-MM-dd")
        : "-",
      txn.transactionName?.trim() || "-",
      formatUniversalNumber(txn.amount),
    ];
  } else {
    // fallback
    headers = ["Transaction Date", "Description", "Amount"];
    fieldExtractor = (txn) => [
      txn.transactionDate
        ? format(new Date(txn.transactionDate), "yyyy-MM-dd")
        : "-",
      txn.transactionName?.trim() || "-",
      formatUniversalNumber(txn.amount),
    ];
  }

  // // Push headers
  rows.push(headers);

  // Push rows
  transactions.forEach((txn) => {
    rows.push(fieldExtractor(txn));
  });

  const worksheet = XLSX.utils.aoa_to_sheet(rows);

  // Optional: Adjust column widths based on number of columns
  worksheet["!cols"] = headers.map((h) => ({
    wch: h.length < 12 ? 12 : h.length + 5,
  }));

  return worksheet;
};



const formatProfitLossForExcel = (data: any) => {
  const rows: any[] = [];

  // Add headers
  rows.push(["Account", "Amount"]);
  rows.push(['', '']);

  let totalRevenue = 0;
  let totalExpense = 0;

  // Process each main category (REVENUE, EXPENSE)
  data.profitData.forEach((category: any) => {
    // Section header (REVENUE or EXPENSE)
    rows.push([category.name, '']);

    // Process subcategories and accounts
    category.children?.forEach((subCategory: any) => {
      subCategory.children?.forEach((account: any) => {
        rows.push([
          account.name,
          formatUniversalNumber(account.balance),
        ]);
        // Parse balance as number for total
        const num = account.balance;
        if (category.name === "REVENUE") {
          totalRevenue += isNaN(num) ? 0 : num;
        }
        if (category.name === "EXPENSE") {
          totalExpense += isNaN(num) ? 0 : num;
        }
      });
    });

    // Blank row after each section
    rows.push(['', '']);
  });

  // Calculate summary values
  const profitBeforeTaxes = totalRevenue - totalExpense;
  const taxes = data.taxes?.amount || 0;
  const netProfit = profitBeforeTaxes - taxes;

  // Add summary rows
  rows.push(['Profit Before Taxes', `$${profitBeforeTaxes.toFixed(2)}`]);
  rows.push(['Taxes', `$${taxes.toFixed(2)}`]);
  rows.push(['Net Profit', `$${netProfit.toFixed(2)}`]);

  // Create worksheet
  const worksheet = XLSX.utils.aoa_to_sheet(rows);

  // Set column widths
  worksheet['!cols'] = [
    { wch: 30 }, // Account
    { wch: 15 }, // Amount
  ];

  return worksheet;

};

const formatBalanceSheetForExcel = (data: any) => {
  const rows: any[] = [];

  // Add headers
  rows.push(["Account", "Amount"]);
  rows.push(['', '']);

  // Process each main category (ASSET, LIABILITY, etc.)
  data.forEach((category: any) => {
    // Section header (ASSET, LIABILITY, etc.)
    rows.push([category.name, '']);

    // Process subcategories and accounts
    category.children?.forEach((subCategory: any) => {
      subCategory.children?.forEach((account: any) => {
        rows.push([
          account.name,
          formatUniversalNumber(account.balance),
        ]);
      });
    });

    // Blank row after each section
    rows.push(['', '']);
  });

  // Create worksheet
  const worksheet = XLSX.utils.aoa_to_sheet(rows);

  // Set column widths
  worksheet['!cols'] = [
    { wch: 30 }, // Account
    { wch: 15 }, // Amount
  ];

  return worksheet;
};

export function formatK(value: number): string {
  if (value < 0) {
    if (Math.abs(value) >= 10000) {
      return `(${currencyCode()}${Math.abs(value / 10000).toFixed(2)}M)`;
    }
    return `(${currencyCode()}${Math.abs(value).toFixed(2)})`;
  }
  if (Math.abs(value) >= 10000) {
    return currencyCode() + (value / 10000).toFixed(2) + "M";
  }
  return currencyCode() + value.toFixed(2);
}

export function formatUniversalNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return ``;
  if (value === 0) return `${currencyCode()}0.00`;

  const absValue = Math.abs(value);
  const isNegative = value < 0;

  // Format the number with commas and 2 decimal places
  const formattedValue = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(absValue);

  // Add currency symbol and handle negative numbers
  const currencySymbol = currencyCode();
  return isNegative ? `(${currencySymbol}${formattedValue})` : `${currencySymbol}${formattedValue}`;
}

const REQUIRED_FIELDS = ['Amount', 'TransactionName', 'TransactionDate'];

const convertExcelDate = (serial: number): string => {
  const excelEpoch = new Date(1899, 11, 30);
  const date = new Date(excelEpoch.getTime() + serial * 86400000);
  return moment(date).format('YYYY-MM-DDT00:00:00');
};

interface ReadResult {
  validRows: any[];
  invalidRow?: {
    rowIndex: number;
    original: any;
    error: string;
  };
  totalRows: number;
}

export const readExcelFile = (
  file: File,
  onProgress?: (percent: number) => void
): Promise<ReadResult> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });

        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const rawData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

        const validRows: any[] = [];
        const total = rawData.length;

        for (let index = 0; index < rawData.length; index++) {
          const row = rawData[index] as Record<string, any>;

          // Check for missing required fields
          const missingField = REQUIRED_FIELDS.find((field) => !(field in row));
          if (missingField) {
            return resolve({
              validRows: [],
              invalidRow: {
                rowIndex: index + 1,
                original: row,
                error: `${missingField} is missing`,
              },
              totalRows: total,
            });
          }

          // Validate Amount
          const amount = row.Amount;
          if (amount === '' || amount === null || amount === undefined || Number(amount) === 0) {
            return resolve({
              validRows: [],
              invalidRow: {
                rowIndex: index + 1,
                original: row,
                error: `Amount is missing or zero`,
              },
              totalRows: total,
            });
          }

          // Validate TransactionDate
          let convertedDate = '';
          if (typeof row.TransactionDate === 'number') {
            convertedDate = convertExcelDate(row.TransactionDate);
          } else if (typeof row.TransactionDate === 'string' && row.TransactionDate.trim()) {
            const date = moment(row.TransactionDate, ['MM/DD/YYYY', 'YYYY-MM-DD', moment.ISO_8601], true);
            if (!date.isValid()) {
              return resolve({
                validRows: [],
                invalidRow: {
                  rowIndex: index + 1,
                  original: row,
                  error: `TransactionDate is invalid`,
                },
                totalRows: total,
              });
            }
            convertedDate = date.format('YYYY-MM-DDT00:00:00');
          } else {
            return resolve({
              validRows: [],
              invalidRow: {
                rowIndex: index + 1,
                original: row,
                error: `TransactionDate is missing or empty`,
              },
              totalRows: total,
            });
          }

          const cleaned = {
            amount,
            transactionName: row.TransactionName,
            transactionDate: convertedDate,
          };

          validRows.push(cleaned);

          // Progress update
          if (onProgress) {
            const percent = Math.round(((index + 1) / total) * 100);
            onProgress(percent);
          }
        }

        resolve({ validRows, totalRows: total });
      } catch (err) {
        reject(err);
      }
    };

    reader.onerror = (error) => reject(error);
    reader.readAsArrayBuffer(file);
  });
};

